import posthog, { type PostHogConfig } from 'posthog-js';
import { LABS_EXCEPTION_CAPTURE } from '../../../../shared/exception-scrubber';
import { prepareBrowserEvent } from './privacy';
import { createSubscriptionEnricher, type GetToken } from '../../../../shared/sgapp-subscription';
import type { AnalyticsEvent } from '../../core/events';

/** Browser analytics, copied from One Step Ahead without its feedback survey. */
export interface Analytics {
  track(event: AnalyticsEvent): void;
  dispose(): void;
}
export const nullAnalytics = (): Analytics => ({ track() {}, dispose() {} });
const quietly = (fn: () => void) => { try { fn(); } catch { /* Measurement must never interrupt teaching. */ } };
const interactions: Partial<PostHogConfig> = {
  capture_pageview: 'history_change', capture_pageleave: true, capture_heatmaps: true,
  autocapture: { dom_event_allowlist: ['click'], capture_copied_text: false },
};
export function createAnalytics(userId: string, email: string | null, getToken: GetToken): Analytics {
  if (!import.meta.env.VITE_POSTHOG_KEY) return nullAnalytics();
  const subscription = createSubscriptionEnricher({ hostname: window.location.hostname, enrichAll: true });
  subscription.setIdentity(userId, getToken);
  let active = true;
  quietly(() => {
    posthog.init(import.meta.env.VITE_POSTHOG_KEY!, {
      api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://s.240.org',
      ui_host: 'https://us.posthog.com',
      defaults: '2026-05-30',
      ...interactions, person_profiles: 'identified_only',
      mask_all_text: true, mask_all_element_attributes: true,
      disable_surveys: true, disable_session_recording: false,
      enable_recording_console_log: false, capture_dead_clicks: false,
      // Errors are on, scrubbed first. See shared/exception-scrubber.ts.
      capture_exceptions: LABS_EXCEPTION_CAPTURE, before_send: prepareBrowserEvent,
      capture_performance: false,
      session_recording: {
        maskAllInputs: true, maskTextSelector: '.ph-mask', blockClass: 'ph-no-capture',
        blockSelector: '.cl-rootBox, .cl-userButtonPopoverCard', recordHeaders: false,
        recordBody: false, recordCrossOriginIframes: false, captureCanvas: { recordCanvas: false },
        maskCapturedNetworkRequestFn: request => ({ ...request, name: request.name.split(/[?#]/)[0]! }),
      },
    });
    // Identified by email, so a colleague testing the tool can be told apart from a teacher (R20).
    posthog.identify(userId, email ? { email } : {});
    // init is a no-op on account changes; restore capture stopped by dispose.
    posthog.set_config(interactions);
    // This shared project's URL trigger targets another product. Start recording locally,
    // retaining the project's sampling and all configured masking/blocking rules.
    posthog.startSessionRecording({ url_trigger: true });
  });
  return {
    track(event) {
      if (!active) return;
      quietly(() => subscription.track(event, (enriched, timestamp) => quietly(() => {
        if (active) posthog.capture(event.name, { ...enriched.properties, product: 'preview_planner', event_source: 'browser', ...(email ? { $set: { email } } : {}) }, timestamp ? { timestamp } : undefined);
      })));
    },
    dispose() {
      active = false; subscription.dispose();
      quietly(() => { posthog.set_config({ autocapture: false, capture_heatmaps: false, capture_pageview: false, capture_pageleave: false }); });
      quietly(() => { posthog.stopSessionRecording(); posthog.reset(); });
    },
  };
}
export function posthogHeaders(): Record<string, string> {
  if (!import.meta.env.VITE_POSTHOG_KEY) return {};
  try {
    const id = posthog.get_distinct_id(); const session = posthog.get_session_id();
    return id && session ? { 'X-POSTHOG-DISTINCT-ID': id, 'X-POSTHOG-SESSION-ID': session } : {};
  } catch { return {}; }
}
